import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { submitGrantApplication } from "../firebase/services";

import { NIGERIA_STATES_LGAS } from "../data/statesLgas";

const STEPS = ["Personal Info", "Project Details", "Impact & Bank", "Review & Submit"];
const STATES = Object.keys(NIGERIA_STATES_LGAS);
const CATEGORIES = ["Education & Literacy","Youth Empowerment","Women & Girls","Health & Sanitation","Agriculture & Food Security","Fishery","Poultry","Poverty Alleviation","Community Infrastructure","Environmental Sustainability","Business Development"];
const GRANT_TYPES = ["Community Seed Grant (₦80K–₦200K)","Startup Growth Grant (₦2M–₦5M)","Development Acceleration Grant (₦5M–₦20M)","Impact Partnership Grant (₦10M–₦50M)","Emergency Relief Grant (₦50K–₦250K)"];
const BANKS = ["Access Bank","First Bank Nigeria","GTBank","UBA","Zenith Bank","Fidelity Bank","Union Bank","Stanbic IBTC","Polaris Bank","Sterling Bank","Wema Bank","Keystone Bank","Heritage Bank","Ecobank Nigeria","FCMB","Jaiz Bank","Unity Bank","Providus Bank","Kuda Bank","Opay"];

export default function ApplyPage() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep]           = useState(0);
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [appRef, setAppRef]       = useState("");
  const [error, setError]         = useState("");

  const [form, setForm] = useState({
    applicantName: userProfile?.name  || "",
    email:         currentUser?.email || "",
    phone:         userProfile?.phone || "",
    state:         userProfile?.state || "",
    orgName:       userProfile?.org   || "",
    orgType:       userProfile?.orgType || "",
    regNumber:     "", lga: "",
    projectTitle:  "", grantType: "", category: "",
    projectDesc:   "", timeline: "", teamSize: "",
    fundingAmount: "", beneficiaries: "", impact: "", sustainability: "",
    accountName:   "", accountNumber: "", bankName: "", bankBranch: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Block unpaid members
  if (userProfile && !userProfile.hasPaidMembership) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--off-white)", padding:"120px 5vw 80px", textAlign:"center" }}>
        <div>
          <div style={{ fontSize:64, marginBottom:20 }}>🔒</div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, color:"var(--navy)", marginBottom:14 }}>Membership Required</h2>
          <p style={{ color:"var(--text-muted)", fontSize:16, lineHeight:1.75, maxWidth:460, margin:"0 auto 28px" }}>
            Only paid members can apply for grants. Complete your ₦250 membership payment to unlock grant applications instantly.
          </p>
          <Link to="/membership" className="btn-primary" style={{ display:"inline-block" }}>Pay Membership & Unlock Grants →</Link>
        </div>
      </div>
    );
  }

  const validateStep = () => {
    if (step === 0) {
      if (!form.applicantName.trim()) return "Full name is required.";
      if (!form.email.trim())         return "Email is required.";
      if (!form.phone.trim())         return "Phone number is required.";
      if (!form.state)                return "Please select your state.";
    }
    if (step === 1) {
      if (!form.projectTitle.trim())  return "Project title is required.";
      if (!form.grantType)            return "Please select a grant type.";
      if (!form.category)             return "Please select a project category.";
      if (!form.projectDesc.trim())   return "Project description is required.";
    }
    if (step === 2) {
      if (!form.fundingAmount)           return "Funding amount is required.";
      if (!form.beneficiaries.trim())    return "Number of beneficiaries is required.";
      if (!form.impact.trim())           return "Expected impact is required.";
      if (!form.accountName.trim())      return "Account name is required.";
      if (!form.accountNumber.trim())    return "Account number is required.";
      if (form.accountNumber.length < 10) return "Account number must be 10 digits.";
      if (!form.bankName)                return "Please select your bank.";
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError("");
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setError("");
    try {
      setLoading(true);
      const id = await submitGrantApplication(currentUser.uid, form);
      setAppRef(`RF-APP-${id.slice(0, 8).toUpperCase()}`);
      setSubmitted(true);
    } catch (err) {
      setError("Submission failed. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--off-white)", padding:"120px 5vw 80px", textAlign:"center" }}>
      <div>
        <div style={{ fontSize:64, marginBottom:20 }}>🎉</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:36, color:"var(--navy)", marginBottom:14 }}>Application Submitted!</h2>
        <p style={{ color:"var(--text-muted)", fontSize:16, lineHeight:1.75, maxWidth:480, margin:"0 auto 28px" }}>
          Your application for <strong>{form.projectTitle}</strong> has been received. Our team will review it within 14–21 business days.
        </p>
        <div style={{ background:"white", border:"1px solid var(--border-light)", padding:"20px 28px", borderRadius:4, display:"inline-block", marginBottom:28 }}>
          <div style={{ fontSize:12, color:"var(--text-muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>Application Reference</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:"#C9963E" }}>{appRef}</div>
        </div>
        <div><button className="btn-primary" onClick={() => navigate("/dashboard")}>Go to Dashboard →</button></div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .apply-hero { background:linear-gradient(160deg,#071428,#0B1F3A); padding:120px 5vw 56px; text-align:center; }
        .apply-hero h1 { font-family:'Playfair Display',serif; font-size:clamp(32px,5vw,56px); font-weight:900; color:white; margin-bottom:14px; }
        .stepper { display:flex; align-items:center; justify-content:center; max-width:700px; margin:36px auto 0; flex-wrap:wrap; gap:0; }
        .step-item { display:flex; align-items:center; }
        .step-circle { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; transition:all 0.3s; flex-shrink:0; }
        .step-circle.done { background:#C9963E; color:#0B1F3A; }
        .step-circle.active { background:white; color:#0B1F3A; box-shadow:0 0 0 3px rgba(255,255,255,0.3); }
        .step-circle.pending { background:rgba(255,255,255,0.1); color:rgba(255,255,255,0.4); border:1px solid rgba(255,255,255,0.15); }
        .step-label { font-size:11px; margin-left:7px; white-space:nowrap; }
        .step-label.active { color:white; font-weight:600; }
        .step-label.done { color:#E8B96A; }
        .step-label.pending { color:rgba(255,255,255,0.35); }
        .step-line { width:32px; height:1px; background:rgba(255,255,255,0.15); margin:0 6px; }
        .apply-body { max-width:760px; margin:0 auto; padding:48px 5vw 90px; }
        .form-card { background:white; border:1px solid var(--border-light); padding:36px; }
        .form-row-2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .section-head { font-family:'Playfair Display',serif; font-size:19px; font-weight:700; color:var(--navy); margin-bottom:20px; padding-bottom:12px; border-bottom:1px solid var(--border-light); }
        .form-nav { display:flex; justify-content:space-between; margin-top:24px; }
        .bank-box { background:rgba(201,150,62,0.04); border:1px solid rgba(201,150,62,0.15); padding:20px; border-radius:4px; margin-top:20px; }
        .bank-box-title { font-size:13px; font-weight:700; color:var(--navy); margin-bottom:14px; }
        .required { color:var(--gold); }
        .review-row { display:flex; justify-content:space-between; padding:11px 0; border-bottom:1px solid var(--border-light); }
        @media(max-width:600px){ .form-row-2{grid-template-columns:1fr;} .step-label{display:none;} }
      `}</style>

      {/* HERO */}
      <section className="apply-hero">
        <span className="section-eyebrow" style={{ color:"#E8B96A" }}>Grant Application</span>
        <h1>Apply for Funding</h1>
        <p style={{ color:"rgba(255,255,255,0.55)", fontSize:15, maxWidth:460, margin:"0 auto" }}>
          Complete all four steps carefully. Ensure your bank details are accurate for disbursement.
        </p>
        <div className="stepper">
          {STEPS.map((s, i) => (
            <div className="step-item" key={s}>
              <div className={`step-circle ${i < step ? "done" : i === step ? "active" : "pending"}`}>{i < step ? "✓" : i + 1}</div>
              <span className={`step-label ${i < step ? "done" : i === step ? "active" : "pending"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="step-line" />}
            </div>
          ))}
        </div>
      </section>

      <div className="apply-body">
        <div className="form-card">
          {error && <div className="error-box">⚠️ {error}</div>}

          {/* STEP 0 — PERSONAL INFO */}
          {step === 0 && (
            <>
              <div className="section-head">Personal Information</div>
              <div className="form-group">
                <label className="form-label">Full Name <span className="required">*</span></label>
                <input className="form-input" placeholder="Your full name" value={form.applicantName} onChange={e => set("applicantName", e.target.value)} />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Email Address <span className="required">*</span></label>
                  <input className="form-input" type="email" value={form.email} onChange={e => set("email", e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number <span className="required">*</span></label>
                  <input className="form-input" type="tel" placeholder="+234 800 000 0000" value={form.phone} onChange={e => set("phone", e.target.value)} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">State <span className="required">*</span></label>
                  <select className="form-select" value={form.state} onChange={e => set("state", e.target.value)}>
                    <option value="">Select state...</option>
                    {STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">LGA</label>
                  <select className="form-select" value={form.lga} onChange={e => set("lga", e.target.value)} disabled={!form.state}>
                    <option value="">Select LGA...</option>
                    {form.state && NIGERIA_STATES_LGAS[form.state]?.map(lga => <option key={lga}>{lga}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop:8, padding:"14px 16px", background:"var(--off-white)", borderRadius:4, fontSize:13, color:"var(--text-muted)" }}>
                💡 <strong>Organization details</strong> are optional for individual applicants. Community groups and NGOs should complete the organization fields below.
              </div>
              <div style={{ marginTop:14 }}>
                <div className="form-group">
                  <label className="form-label">Organization / NGO Name</label>
                  <input className="form-input" placeholder="Your organization (if applicable)" value={form.orgName} onChange={e => set("orgName", e.target.value)} />
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Organization Type</label>
                    <select className="form-select" value={form.orgType} onChange={e => set("orgType", e.target.value)}>
                      <option value="">Select type...</option>
                      <option>Registered NGO</option>
                      <option>Community Group</option>
                      <option>Social Enterprise</option>
                      <option>Individual Activist</option>
                      <option>Faith-Based Organization</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">CAC Registration No.</label>
                    <input className="form-input" placeholder="RC/IT number" value={form.regNumber} onChange={e => set("regNumber", e.target.value)} />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* STEP 1 — PROJECT DETAILS */}
          {step === 1 && (
            <>
              <div className="section-head">Project Details</div>
              <div className="form-group">
                <label className="form-label">Project Title <span className="required">*</span></label>
                <input className="form-input" placeholder="Clear, descriptive project title" value={form.projectTitle} onChange={e => set("projectTitle", e.target.value)} />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Grant Type <span className="required">*</span></label>
                  <select className="form-select" value={form.grantType} onChange={e => set("grantType", e.target.value)}>
                    <option value="">Select grant...</option>
                    {GRANT_TYPES.map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Category <span className="required">*</span></label>
                  <select className="form-select" value={form.category} onChange={e => set("category", e.target.value)}>
                    <option value="">Select category...</option>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Project Description <span className="required">*</span></label>
                <textarea className="form-textarea" rows={5} placeholder="Describe your project: what you will do, who it serves, and how it will be implemented..." value={form.projectDesc} onChange={e => set("projectDesc", e.target.value)} />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Timeline</label>
                  <select className="form-select" value={form.timeline} onChange={e => set("timeline", e.target.value)}>
                    <option value="">Select duration...</option>
                    {["1–3 months","3–6 months","6–12 months","12–24 months"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Team Size</label>
                  <input className="form-input" placeholder="Number of staff/volunteers" value={form.teamSize} onChange={e => set("teamSize", e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* STEP 2 — IMPACT & BANK */}
          {step === 2 && (
            <>
              <div className="section-head">Impact & Bank Details</div>
              <div className="form-group">
                <label className="form-label">Funding Amount Requested (₦) <span className="required">*</span></label>
                <input className="form-input" type="number" placeholder="e.g. 150000" value={form.fundingAmount} onChange={e => set("fundingAmount", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Number of Beneficiaries <span className="required">*</span></label>
                <input className="form-input" placeholder="e.g. 500 community members, 200 students" value={form.beneficiaries} onChange={e => set("beneficiaries", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Expected Community Impact <span className="required">*</span></label>
                <textarea className="form-textarea" rows={4} placeholder="What specific changes will this project create?" value={form.impact} onChange={e => set("impact", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Sustainability Plan</label>
                <textarea className="form-textarea" rows={3} placeholder="How will this project continue after the grant period?" value={form.sustainability} onChange={e => set("sustainability", e.target.value)} />
              </div>

              {/* BANK DETAILS */}
              <div className="bank-box">
                <div className="bank-box-title">🏦 Bank Account for Disbursement</div>
                <p style={{ fontSize:13, color:"var(--text-muted)", marginBottom:16, lineHeight:1.6 }}>
                  Approved funds will be sent directly to this account. Please ensure all details are accurate.
                </p>
                <div className="form-group">
                  <label className="form-label">Account Name <span className="required">*</span></label>
                  <input className="form-input" placeholder="Full name as on bank account" value={form.accountName} onChange={e => set("accountName", e.target.value)} />
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Account Number <span className="required">*</span></label>
                    <input className="form-input" type="tel" placeholder="10-digit account number" maxLength={10} value={form.accountNumber} onChange={e => set("accountNumber", e.target.value.replace(/\D/g, ""))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Bank Name <span className="required">*</span></label>
                    <select className="form-select" value={form.bankName} onChange={e => set("bankName", e.target.value)}>
                      <option value="">Select bank...</option>
                      {BANKS.map(b => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Bank Branch</label>
                  <input className="form-input" placeholder="Branch name or location (optional)" value={form.bankBranch} onChange={e => set("bankBranch", e.target.value)} />
                </div>
                <div style={{ background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.2)", padding:"10px 14px", borderRadius:4, fontSize:12, color:"#16a34a" }}>
                  🔒 Your bank details are encrypted and only used for grant disbursement. We never charge your account.
                </div>
              </div>
            </>
          )}

          {/* STEP 3 — REVIEW */}
          {step === 3 && (
            <>
              <div className="section-head">Review Your Application</div>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, color:"var(--gold)", fontWeight:600, letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Personal Information</div>
                {[["Applicant",form.applicantName],["Email",form.email],["Phone",form.phone],["State",form.state],["Organization",form.orgName||"N/A"]].map(([l,v])=>(
                  <div className="review-row" key={l}>
                    <span style={{ fontSize:13, color:"var(--text-muted)" }}>{l}</span>
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--navy)" }}>{v||"—"}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, color:"var(--gold)", fontWeight:600, letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Project Details</div>
                {[["Project Title",form.projectTitle],["Grant Type",form.grantType],["Category",form.category],["Timeline",form.timeline],["Funding Requested",form.fundingAmount?`₦${Number(form.fundingAmount).toLocaleString()}`:"—"],["Beneficiaries",form.beneficiaries]].map(([l,v])=>(
                  <div className="review-row" key={l}>
                    <span style={{ fontSize:13, color:"var(--text-muted)" }}>{l}</span>
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--navy)", textAlign:"right", maxWidth:"60%" }}>{v||"—"}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, color:"var(--gold)", fontWeight:600, letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Bank Account</div>
                {[["Account Name",form.accountName],["Account Number",form.accountNumber],["Bank",form.bankName],["Branch",form.bankBranch||"N/A"]].map(([l,v])=>(
                  <div className="review-row" key={l}>
                    <span style={{ fontSize:13, color:"var(--text-muted)" }}>{l}</span>
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--navy)" }}>{v||"—"}</span>
                  </div>
                ))}
              </div>
              <div style={{ background:"rgba(201,150,62,0.05)", border:"1px solid rgba(201,150,62,0.2)", padding:"14px 18px", borderRadius:2, fontSize:13, color:"var(--navy)", lineHeight:1.7 }}>
                By submitting, I confirm that all information provided is accurate and truthful. I understand that submitting false information will result in immediate disqualification. I agree to the <Link to="/terms" style={{ color:"var(--gold)" }}>Terms & Conditions</Link>.
              </div>
            </>
          )}

          {/* NAVIGATION */}
          <div className="form-nav">
            {step > 0
              ? <button className="btn-outline-dark" onClick={() => { setError(""); setStep(s => s - 1); }}>← Back</button>
              : <div />
            }
            {step < STEPS.length - 1
              ? <button className="btn-primary" onClick={handleNext}>Continue →</button>
              : <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? <><span className="spinner-white" />Submitting...</> : "Submit Application ✓"}
                </button>
            }
          </div>
        </div>
      </div>
    </>
  );
}
